import { useState } from 'react';
import toast from 'react-hot-toast';
import { farmbaseApi } from '../../services/api';
import { useUnitStore } from '../../store/unitStore';
import { apiErrorMessage } from '../../lib/apiError';
import GeometryPicker from './GeometryPicker';
import type { Farmbase, Geometry } from '../../types/domain';

const field = 'w-full mt-0.5 px-2 py-1.5 min-h-11 sm:min-h-0 rounded bg-command-bg border border-command-border text-sm text-gray-100';
const lbl = 'text-[11px] text-gray-400 font-tactical';

export default function FarmbaseForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial?: Farmbase | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { units } = useUnitStore();
  const playerUnits = units.filter((u) => u.role === 'PLAYER');
  const [name, setName] = useState(initial?.name ?? '');
  const [commanderName, setCommanderName] = useState(initial?.commanderName ?? '');
  const [officers, setOfficers] = useState(initial?.manpowerOfficers ?? 0);
  const [jcos, setJcos] = useState(initial?.manpowerJCOs ?? 0);
  const [ors, setOrs] = useState(initial?.manpowerORs ?? 0);
  const [unitId, setUnitId] = useState(initial?.unitId ?? playerUnits[0]?.id ?? '');
  // Geometry is NESTED on the DTO (§16): seed straight from `initial.geometry`.
  // (The old flat-field reconstruction read undefined props and showed an empty picker
  // after a successful save.)
  const [geometry, setGeometry] = useState<Geometry | null>(initial?.geometry ?? null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !unitId) {
      toast.error('Name and unit are required');
      return;
    }
    setBusy(true);
    try {
      // Geometry is a NESTED object on the wire (§15/§16): `geometry: {…}` sets it,
      // `geometry: null` clears it. Never spread the fields flat onto the body.
      const body = {
        name: name.trim(),
        commanderName: commanderName.trim() || null,
        manpowerOfficers: officers,
        manpowerJCOs: jcos,
        manpowerORs: ors,
        unitId,
        geometry: geometry ?? null,
      };
      if (initial) await farmbaseApi.update(initial.id, body);
      else await farmbaseApi.create(body);
      toast.success(`Farmbase ${initial ? 'updated' : 'created'}`);
      onSaved();
    } catch (err: any) {
      // Surface the first validation detail so the toast is actionable.
      toast.error(apiErrorMessage(err, 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className={lbl}>
          Name
          <input className={field} value={name} onChange={(e) => setName(e.target.value)} aria-label="Farmbase name" />
        </label>
        <label className={lbl}>
          Commander
          <input className={field} value={commanderName} onChange={(e) => setCommanderName(e.target.value)} aria-label="Commander name" />
        </label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <label className={lbl}>
          Officers
          <input type="number" min={0} className={field} value={officers} onChange={(e) => setOfficers(Number(e.target.value))} aria-label="Officers" />
        </label>
        <label className={lbl}>
          JCOs
          <input type="number" min={0} className={field} value={jcos} onChange={(e) => setJcos(Number(e.target.value))} aria-label="JCOs" />
        </label>
        <label className={lbl}>
          ORs
          <input type="number" min={0} className={field} value={ors} onChange={(e) => setOrs(Number(e.target.value))} aria-label="ORs" />
        </label>
        <label className={lbl}>
          Unit
          <select className={field} value={unitId} onChange={(e) => setUnitId(e.target.value)} aria-label="Unit">
            {playerUnits.map((u) => (
              <option key={u.id} value={u.id}>{u.code}</option>
            ))}
          </select>
        </label>
      </div>
      <div>
        <div className={lbl}>Geometry</div>
        <GeometryPicker value={geometry} onChange={setGeometry} />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-4 py-2.5 sm:py-1.5 rounded-lg text-sm text-gray-400 border border-command-border touch-manipulation">Cancel</button>
        <button type="submit" disabled={busy} className="px-5 py-2.5 sm:py-1.5 rounded-lg text-sm font-semibold bg-command-accent text-command-bg disabled:opacity-50 touch-manipulation">
          {busy ? 'Saving…' : 'Save Farmbase'}
        </button>
      </div>
    </form>
  );
}
